import { useDroppable } from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { type MouseEvent } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  selectSelectedInteractionIds,
  setPrimaryInteraction,
  setSelectedInteraction,
  setSelectedTake,
} from "../../app/slices/TrainingSlice";
import Take3D from "../../models/Take3D";
import { EditorColumnInlineAddButton } from "../editor_columns/EditorColumn";
import EditorInteractionRow from "../editor_columns/EditorInteractionRow";
import InteractionPicker from "../interaction_picker/InteractionPicker";
import InteractionContextMenu from "../interaction_context_menu/InteractionContextMenu";
import { useScenarioBuilderInteractionList } from "./ScenarioBuilderInteractionList.hook";
import {
  getInteractionDndId,
  getInteractionListDndId,
  TakeInteractionDndData,
  useTakeInteractionDndContext,
} from "../training_timeline/TakeInteractionDndProvider";

export default function ScenarioBuilderInteractionList(props: {
  take?: Take3D;
}) {
  const { take } = props;
  const dispatch = useDispatch();
  const selectedInteractionIds = useSelector(selectSelectedInteractionIds);
  const {
    isInteractionPickerOpen,
    setIsInteractionPickerOpen,
    openInteractionPicker,
  } = useScenarioBuilderInteractionList(take);
  const droppableTakeId = take?.id ?? "scenario-builder";
  const { hoverTakeId } = useTakeInteractionDndContext();
  const isHoverDropTarget = !!take && hoverTakeId === take.id;
  const { setNodeRef } = useDroppable({
    id: getInteractionListDndId(droppableTakeId),
    data: take
      ? ({ kind: "interaction-list", takeId: take.id } as TakeInteractionDndData)
      : undefined,
  });

  const handleContextMenu = (event: MouseEvent<HTMLDivElement>) => {
    if (!take) return;

    const row = (event.target as HTMLElement).closest<HTMLElement>(
      "[data-interaction-id]"
    );
    const interactionId = row?.dataset.interactionId;

    if (interactionId) {
      // a row inside the multi-selection keeps the group intact
      dispatch(
        selectedInteractionIds.includes(interactionId)
          ? setPrimaryInteraction(interactionId)
          : setSelectedInteraction(interactionId)
      );
      return;
    }

    dispatch(setSelectedTake(take.id));
  };

  return (
    <InteractionContextMenu>
      <div className="space-y-0.5" onContextMenu={handleContextMenu}>
        <SortableContext
            items={take?.interactions.map((interaction) => getInteractionDndId(interaction.id)) ?? []}
            strategy={verticalListSortingStrategy}
            id={take?.id ?? "scenario-builder-interactions"}
          >
            <div
              ref={setNodeRef}
              className={`min-h-[34px] space-y-0.5 rounded-5 ${
                isHoverDropTarget
                  ? "ring-1 ring-creator-primary bg-creator-primary-washed"
                  : ""
              }`}
              data-testid="scenario-builder-interactions"
              data-drop-target={isHoverDropTarget ? "true" : undefined}
            >
              {take && take.interactions.length > 0 ? (
                take.interactions.map((interaction) => (
                  <EditorInteractionRow
                    key={interaction.id}
                    interaction={interaction}
                    takeId={take.id}
                    testIdPrefix="scenario-row-interaction"
                  />
                ))
              ) : (
                <div className="px-2 py-2 text-[13px] font-normal italic text-creator-text-sub">
                  No interactions in this take
                </div>
              )}
            </div>
        </SortableContext>
        <EditorColumnInlineAddButton
          label="Add interaction"
          onClick={openInteractionPicker}
          disabled={!take}
        />
        <InteractionPicker
          open={isInteractionPickerOpen}
          onClose={() => setIsInteractionPickerOpen(false)}
        />
      </div>
    </InteractionContextMenu>
  );
}
